import React, { useEffect, useState } from 'react';

import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import Select from 'react-select/creatable';
import { useDebounce } from 'use-debounce';

import {
  backgroundGrey5,
  blackberry,
  coolGrey,
  grape,
  greyStroke,
  purple1,
  squidInk,
  white,
} from 'lib/css/colors';
import Container from 'components/shared/Container';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import { SORT_DIRECTIONS } from 'lib/constants';
import {
  SQS_ATTRIBUTE_MESSAGE_COUNT,
  TASKS,
  TASKS_COUNT_BY_STATUS,
  TASKS_ANALYZE_P0_SYNC_GAPS,
  EXPORT_DATA_CUT_DRY,
} from 'lib/networking/endpoints';
import SwalDialog from 'lib/utils/toast';
import Cookies from 'lib/enums/Cookies';
import { TaskStatus, TaskStatusOptions, TaskTypeOptions } from 'lib/enums/TaskEnums';
import TaskTableRow from 'components/settings/team/tasks/TaskTableRow';
import P0SyncDatePickerModal from './P0SyncDatePickerModal';
import P0SyncGapsModal from './P0SyncGapsModal';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import useLocalStorage from 'lib/hooks/useLocalStorage';

function TasksView() {
  const [cachedFilters, setCachedFilters] = useLocalStorage(Cookies.TASK_VIEW_FILTERS, {});
  const [selectedTaskStatus, setSelectedTaskStatus] = useState(cachedFilters.taskStatus || '');
  const [selectedTaskType, setSelectedTaskType] = useState(cachedFilters.taskType || '');
  const [dataSourceId, setDataSourceId] = useState(cachedFilters.dataSourceId || '');
  const [searchedDatasourceId] = useDebounce(dataSourceId, 600);
  const [taskId, setTaskId] = useState(cachedFilters.taskId || '');
  const [searchedTaskId] = useDebounce(taskId, 600);
  const [showDatePickerModal, setShowDatePickerModal] = useState(false);
  const [showGapsModal, setShowGapsModal] = useState(false);
  const [gaps, setGaps] = useState([]);
  const [selectedAnalysisDate, setSelectedAnalysisDate] = useState(null);

  useEffect(() => {
    setCachedFilters({
      taskStatus: selectedTaskStatus,
      taskType: selectedTaskType,
      taskId,
      dataSourceId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTaskStatus, selectedTaskType, taskId, dataSourceId]);

  const {
    data: { data: tasks = [] } = [],
    loading,
    error,
    refetch,
  } = useGet(TASKS, {
    order_by: SORT_DIRECTIONS.DESC,
    limit: 100,
    task_status: selectedTaskStatus,
    task_type: selectedTaskType,
    data_source_id: searchedDatasourceId,
    task_id: searchedTaskId,
  });

  const { data: { count: msgCount = 0 } = 0 } = useGet(SQS_ATTRIBUTE_MESSAGE_COUNT, {});

  const { data: { count: queuedTaskCount = 0 } = 0 } = useGet(TASKS_COUNT_BY_STATUS, {
    task_status: TaskStatus.QUEUED,
  });

  const { postData: analyzeP0SyncGaps, loading: analyzingGaps } = usePost(
    TASKS_ANALYZE_P0_SYNC_GAPS,
    response => {
      setGaps(response.data?.gaps || []);
      setShowDatePickerModal(false);
      setShowGapsModal(true);
      if (response.data.gaps && response.data.gaps?.length > 0) {
        SwalDialog(
          'info',
          `Found ${response.data.gaps.length} gap(s) in P0 sync execution`,
          'Analysis Complete',
          'center',
        );
      } else {
        SwalDialog(
          'success',
          'No gaps found! All time periods are covered.',
          'Analysis Complete',
          'center',
        );
      }
    },
    () => {
      SwalDialog('error', 'Error occurred while analyzing P0 sync gaps', 'Error', 'center');
      setShowDatePickerModal(false);
    },
  );

  const { postData: createP0SyncTask, loading: creatingTask } = usePost(
    EXPORT_DATA_CUT_DRY,
    response => {
      SwalDialog(
        'success',
        `P0 sync task created successfully (Task ID: ${response.task_id})`,
        'Success',
        'center',
      );
      refetch();
    },
    () => {
      SwalDialog('error', 'Error occurred while creating P0 sync task', 'Error', 'center');
    },
  );

  const handleDateSelection = selectedDate => {
    setSelectedAnalysisDate(selectedDate);
    const dateString = selectedDate.toISOString().split('T')[0];
    analyzeP0SyncGaps({
      selected_date: dateString,
    });
  };

  const handleCreateTask = async gap => {
    await createP0SyncTask({
      export_type: 'p0_attributes',
      is_recurring_trigger: true,
      end_time: gap.end,
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return null;
  }

  return (
    <div className={css(styles.pageContainer)}>
      <div className={css(styles.headerSection)}>
        <div className={css(styles.headerContent)}>
          <h1 className={css(styles.pageTitle)}>Tasks</h1>
        </div>
      </div>

      <Container className={css(styles.mainContent)}>
        <Row className={css(styles.toolbarRow)}>
          <Col xs={12} sm={6} md={2} className={css(styles.filterCol)}>
            <Select
              placeholder="Select Status"
              isClearable
              options={TaskStatusOptions}
              onChange={option => setSelectedTaskStatus(option?.value || '')}
              value={TaskStatusOptions.find(option => option.value === selectedTaskStatus) || ''}
            />
          </Col>
          <Col xs={12} sm={6} md={2} className={css(styles.filterCol)}>
            <Select
              placeholder="Select Task Type"
              isClearable
              options={TaskTypeOptions}
              onChange={option => setSelectedTaskType(option?.value || '')}
              value={TaskTypeOptions.find(option => option.value === selectedTaskType) || ''}
            />
          </Col>
          <Col xs={12} sm={6} md={2} className={css(styles.filterCol)}>
            <Form.Control
              type="number"
              onChange={e => setTaskId(e.target.value)}
              defaultValue={taskId}
              placeholder="Task Id"
            />
          </Col>
          <Col xs={12} sm={6} md={2} className={css(styles.filterCol)}>
            <Form.Control
              onChange={e => setDataSourceId(e.target.value)}
              defaultValue={dataSourceId}
              placeholder="Data Source Id"
            />
          </Col>
          <Col xs={12} sm={6} md={4} className={css(styles.actionCol)}>
            <Button
              className={css(styles.analyzeButton)}
              onClick={() => setShowDatePickerModal(true)}
              disabled={analyzingGaps}
            >
              {analyzingGaps ? 'Analyzing...' : 'Analyze P0 Sync Gaps'}
            </Button>
            <div className={css(styles.statsChip)}>
              <span className={css(styles.statsLabel)}>SQS</span>
              <span className={css(styles.statsValue)}>{msgCount}</span>
            </div>
            <div className={css(styles.statsChip)}>
              <span className={css(styles.statsLabel)}>Queued</span>
              <span className={css(styles.statsValue)}>{queuedTaskCount}</span>
            </div>
          </Col>
        </Row>

        <div className={css(styles.scrollableContainer)}>
          <Table striped bordered hover className={css(styles.table)}>
            <thead>
              <tr>
                <th className={css(styles.th)}>Task Id</th>
                <th className={css(styles.th)}>Created time</th>
                <th className={css(styles.th)}>Task type</th>
                <th className={css(styles.th)}>Data Source Id</th>
                <th className={css(styles.th)}>Process Id</th>
                <th className={css(styles.th)}>Instance Id</th>
                <th className={css(styles.th)}>Status</th>
                <th className={css(styles.th)}>Retries</th>
                <th className={css(styles.th)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks
                ? tasks.map(task => <TaskTableRow key={task.id} task={task} refetch={refetch} />)
                : null}
            </tbody>
          </Table>
        </div>
      </Container>

      {showDatePickerModal && (
        <P0SyncDatePickerModal
          show={showDatePickerModal}
          onHide={() => setShowDatePickerModal(false)}
          onDateSelect={handleDateSelection}
        />
      )}

      {showGapsModal && selectedAnalysisDate && (
        <P0SyncGapsModal
          show={showGapsModal}
          onHide={() => setShowGapsModal(false)}
          gaps={gaps}
          selectedDate={selectedAnalysisDate}
          onCreateTask={handleCreateTask}
          loading={creatingTask}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: backgroundGrey5,
  },
  headerSection: {
    background: `linear-gradient(135deg, ${blackberry} 0%, ${purple1} 100%)`,
    padding: '24px 40px',
    '@media (max-width: 576px)': {
      padding: '16px 20px',
    },
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: 1600,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 600,
    color: white,
    margin: 0,
    '@media (max-width: 576px)': {
      fontSize: 22,
    },
  },
  mainContent: {
    padding: '24px 40px',
    '@media (max-width: 576px)': {
      padding: '16px 16px',
    },
  },
  toolbarRow: {
    marginBottom: 16,
    alignItems: 'center',
  },
  filterCol: {
    marginBottom: 8,
  },
  actionCol: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
    '@media (max-width: 576px)': {
      justifyContent: 'flex-start',
    },
  },
  analyzeButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '8px 14px',
    fontWeight: 600,
    fontSize: 13,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: grape,
    },
  },
  statsChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    backgroundColor: white,
    border: `1px solid ${greyStroke}`,
    borderRadius: 8,
    padding: '6px 12px',
    whiteSpace: 'nowrap',
  },
  statsLabel: {
    fontSize: 12,
    color: coolGrey,
    fontWeight: 500,
  },
  statsValue: {
    fontSize: 13,
    color: squidInk,
    fontWeight: 700,
  },
  scrollableContainer: {
    backgroundColor: white,
    borderRadius: 12,
    border: `1px solid ${greyStroke}`,
    overflowX: 'auto',
    marginTop: 12,
  },
  table: {
    marginBottom: 0,
    minWidth: 800,
  },
  th: {
    color: coolGrey,
    fontWeight: 600,
    fontSize: 13,
    whiteSpace: 'nowrap',
    backgroundColor: backgroundGrey5,
  },
});

export default TasksView;
